/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { ScrollToMode } from '@fluentui/react/lib/List';
import { IScrollablePaneStyleProps, IScrollablePaneStyles, ScrollablePane, ScrollbarVisibility } from '@fluentui/react/lib/ScrollablePane';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { ScoreFilter } from 'src/luis/projects/utterances/types/Filters';
import { LuisId } from 'src/luis/types/Resource';
import { markdownTextToHtmlElements } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerAnswerColumnRenderer';
import { QnaMakerChatTestBladeOptions } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerChatTestBladeOptions';
import { QnaMakerChatTestMessage } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerChatTestMessage';
import { LogicalOperation } from 'src/qnaMaker/core/services/qnaMakerProjectSwaggers';
import { QnaMakerMetadataType } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

type QnaMakerChatTestBladeProps = { onInspectClick?: (qnaId: LuisId) => void; flattenMetadata?: Set<string> };

const messages = defineMessages({
    clear: { id: 'qna.qnaMakerChatTestBlade.clear', defaultMessage: 'Clear' },
    clearButtonAriaLabel: { id: 'qna.qnaMakerChatTestBlade.clearButtonAriaLabel', defaultMessage: 'Clear chat messages.' },
    clearAnnouncerMessage: { id: 'qna.qnaMakerChatTestBlade.clearAnnouncerMessage', defaultMessage: 'Chat messages deleted.' },
    typeMessagePlaceholder: { id: 'qna.qnaMakerChatTestBlade.typeMessagePlaceholder', defaultMessage: 'Type your message and press enter' },
    send: {
        id: 'qna.qnaMakerChatTestBlade.send',
        defaultMessage: 'Send'
    }
});

const theme = getScopedTheme('QnaMakerChatTestBlade');
const textFieldStyles = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>()({
    suffix: { backgroundColor: 'transparent', padding: 0 }
});
const getContainerStyles = (applyMinHeight: boolean) =>
    classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
        root: { border: `1px solid ${theme.defaultBorderColor}`, minHeight: applyMinHeight ? 0 : 'unset' }
    });
const scrollableContainerStyles = classNamesFunction<IScrollablePaneStyleProps, IScrollablePaneStyles>()({
    root: { minHeight: 0, position: 'relative', width: '100%' },
    contentContainer: { position: 'relative', maxHeight: '100%' }
});

export const QnaMakerChatTestBlade = observer((props: QnaMakerChatTestBladeProps) => {
    const { onInspectClick, flattenMetadata } = props;
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { dispatcher, rootStore } = useGlobalState();
    const { announcedComponent, announceAction } = useAnnounced();
    const predictTracker = useProgressTracker({
        onComplete: () => {
            const lastMessage = testPanePredictionStoreHouse.items[testPanePredictionStoreHouse.items.length - 1];
            document.querySelector(`#message_${lastMessage.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            announceAction(lastMessage.answers[0].text);
        }
    });

    const [chatInput, setChatInput] = React.useState('');
    const [metadata, setMetadata] = React.useState<QnaMakerMetadataType>({});
    const [score, setScore] = React.useState<ScoreFilter>({ min: 0, max: 1 });
    const [includeShortAnswer, setIncludeShortAnswer] = React.useState(false);
    const [usePublishedVersion, setUsePublishedVersion] = React.useState(false);
    const [filterOperation, setFilterOperation] = React.useState<LogicalOperation>('AND');
    const [previousUserQuery, setPreviousUserQuery] = React.useState('');

    const { name: projectName, languageDirection, testPanePredictionStoreHouse } = rootStore.currentQnaMakerProjectStore;

    const getPreviousQnaId = () => {
        const itemLength = testPanePredictionStoreHouse.items.length;

        return itemLength ? testPanePredictionStoreHouse.items[itemLength - 1].answers[0].id : -1;
    };

    const submitQuestion = (question?: string, qnaId?: number) => {
        setChatInput('');
        dispatcher.dispatch('predictQnA', {
            metadata,
            projectName,
            toastData: {},
            includeShortAnswer,
            tracker: predictTracker,
            question: question || chatInput,
            logicalOperation: filterOperation,
            useDeployedQnA: usePublishedVersion,
            confidenceScoreThreshold: score.min,
            previousQnAId: getPreviousQnaId(),
            previousUserQuery,
            qnaId
        });

        setPreviousUserQuery(question || chatInput);
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 5 }}>
            <Stack grow styles={getContainerStyles(isXLargeOrGreater)}>
                <QnaMakerChatTestBladeOptions
                    scoreFilter={score}
                    metaData={metadata}
                    onChangeScoreFilter={setScore}
                    onChangeMetadata={setMetadata}
                    filterOperation={filterOperation}
                    includeShortAnswer={includeShortAnswer}
                    usePublishedVersion={usePublishedVersion}
                    onChangeFilterOperation={setFilterOperation}
                    onChangeIncludeShortAnswer={setIncludeShortAnswer}
                    onChangeUsePublishedVersion={setUsePublishedVersion}
                    flattenMetadata={flattenMetadata}
                />

                <Stack horizontal horizontalAlign="end">
                    <ActionButton
                        text={localize(messages.clear)}
                        iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
                        ariaLabel={localize(messages.clearButtonAriaLabel)}
                        title={localize(messages.clearButtonAriaLabel)}
                        onClick={() => {
                            dispatcher.dispatch('clearTestQnAs', { projectName });
                            announceAction(localize(messages.clearAnnouncerMessage));
                        }}
                    />
                </Stack>

                <Stack grow styles={{ root: { overflowY: 'hidden' } }} verticalAlign="end">
                    <ScrollablePane
                        styles={scrollableContainerStyles}
                        initialScrollPosition={ScrollToMode.bottom}
                        scrollbarVisibility={ScrollbarVisibility.auto}>
                        <Stack verticalFill verticalAlign="end" tokens={{ childrenGap: 20, padding: '20px 15px' }}>
                            {testPanePredictionStoreHouse.items.map(qna => (
                                <Stack id={`message_${qna.id}`} key={getId()}>
                                    <QnaMakerChatTestMessage
                                        isQuestionMessage
                                        message={qna.question}
                                        onInspectClick={() => onInspectClick(qna.id)}
                                    />
                                    {includeShortAnswer && qna.answers[0].answerSpan && (
                                        <QnaMakerChatTestMessage
                                            isShortMessage
                                            isQuestionMessage={false}
                                            showMessageTitle={includeShortAnswer}
                                            message={markdownTextToHtmlElements(qna.answers[0].answerSpan.text)}
                                        />
                                    )}
                                    {qna.answers.length && (
                                        <>
                                            {qna.answers[0].text.trim() && (
                                                <QnaMakerChatTestMessage
                                                    className="ck ck-content"
                                                    isQuestionMessage={false}
                                                    message={markdownTextToHtmlElements(qna.answers[0].text)}
                                                    showMessageTitle={includeShortAnswer}
                                                />
                                            )}
                                            <Stack tokens={{ childrenGap: 6, padding: '6px 0 0 16px' }}>
                                                {qna.answers[0].prompts?.length
                                                    ? qna.answers[0].prompts
                                                          .sort((a, b) => a.displayOrder - b.displayOrder)
                                                          .map(p => (
                                                              <QnaMakerChatTestMessage
                                                                  key={p.qnaId}
                                                                  isFollowUpPrompt
                                                                  showMessageTitle={false}
                                                                  isQuestionMessage={false}
                                                                  onFollowUpPromptClicked={() => {
                                                                      setChatInput(p.displayText);
                                                                      submitQuestion(p.displayText, p.qnaId);
                                                                  }}
                                                                  message={markdownTextToHtmlElements(p.displayText)}
                                                              />
                                                          ))
                                                    : null}
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                    </ScrollablePane>
                </Stack>
            </Stack>

            {predictTracker.isRunning && (
                <Stack verticalAlign="center" horizontal horizontalAlign="start" tokens={{ childrenGap: 10 }}>
                    <Spinner size={SpinnerSize.small} />
                    <LuisText>{localize(globalMessages.loading)}</LuisText>
                </Stack>
            )}

            <LuisTextField
                value={chatInput}
                dir={languageDirection}
                styles={textFieldStyles}
                onEnterKeyUp={() => !predictTracker.isRunning && submitQuestion(null)}
                data-automation-id="submitQuestion"
                onChange={(_, newValue) => setChatInput(newValue)}
                placeholder={localize(messages.typeMessagePlaceholder)}
                onRenderSuffix={() => (
                    <IconButton
                        ariaLabel={localize(messages.send)}
                        title={localize(messages.send)}
                        onClick={() => submitQuestion(null)}
                        iconProps={{ iconName: 'Send' }}
                        disabled={predictTracker.isRunning || !chatInput}
                        styles={{ root: { height: '100%' } }}
                    />
                )}
            />

            <div aria-live="assertive">{announcedComponent}</div>
        </Stack>
    );
});
